import React, { useMemo, useState } from 'react';
import {
  Box, Chip, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  Bar, CartesianGrid, Cell, ComposedChart, LabelList, Line, ReferenceLine, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import { LossSeries, DEFAULT_MB_MARGIN_PCT } from '../utils/massBalanceStore';

/**
 * Mass Gain/Loss — the site's overall process health, first thing on the Mass Balance Utility page.
 *
 * Modelled on the Meter Health Overview: a headline figure, then the detail underneath. Format
 * follows the chart EET already use at Toledo — a bar per month, value printed on each bar, green
 * inside the control limits and red outside, with dashed upper and lower limits drawn from the
 * 12-month rolling average.
 *
 * SIGN: a loss plots NEGATIVE, matching that chart. The stored figure is positive for a loss
 * (0.31% means 0.31% lost), so it is negated here. A positive bar is therefore a genuine gain —
 * physically suspect, and worth it being visibly the other side of the axis.
 *
 * The MT/% toggle is Leslie's: percent is the headline unit, tonnes for when the absolute size
 * matters. Percent is of crude processed, the same denominator the Fuel & Loss panel uses, so the
 * two agree.
 */

interface Props {
  series: LossSeries | null;
  /** Mass unit for this dataset, e.g. MT or lbs. */
  massUnit: string;
  /** Months to show. */
  months?: number;
  /** The dataset's configured loss TARGET (shown as "Target" since 15 Oct; still `margin` in code and
   *  storage), as a PERCENT of crude (0.5 = +/-0.5%). Decides green vs
   *  red; the statistical +/-2 sigma band stays on the chart as a reference only. Null/undefined uses
   *  DEFAULT_MB_MARGIN_PCT, so every dataset is judged against a real target from the start. */
  marginPct?: number | null;
}

const GREEN = '#2e7d32';
const RED = '#c0392b';

const MassGainLossOverview: React.FC<Props> = ({ series, massUnit, months = 12, marginPct }) => {
  const theme = useTheme();
  const [mode, setMode] = useState<'pct' | 'mass'>('pct');

  const model = useMemo(() => {
    // The WHOLE series is kept for the rolling average: a trailing 12-month mean needs the eleven
    // months before the first bar, and EET's trend reaches back further than the window shown.
    const all = (series?.periods || []).filter((p) => p.loss_pct != null);
    if (all.length < 2) return null;
    const allPts = all.map((p) => ({
      period: p.period,
      // Negated: a loss is a negative movement in the balance.
      pct: -(p.loss_pct as number),
      mass: p.unaccounted_loss != null ? -p.unaccounted_loss : null,
      crude: p.crude_processed ?? null,
    }));
    const rollAt = (i: number, key: 'pct' | 'mass') => {
      const win = allPts.slice(Math.max(0, i - 11), i + 1)
        .map((q) => q[key]).filter((v): v is number => v != null);
      return win.length ? win.reduce((a, b) => a + b, 0) / win.length : null;
    };
    const rolling = allPts.map((_, i) => ({ pct: rollAt(i, 'pct'), mass: rollAt(i, 'mass') }));
    const from = Math.max(0, allPts.length - months);
    const pts = allPts.slice(from).map((q, j) => ({ ...q, roll: rolling[from + j] }));

    // Limits from the 12-month rolling average, as EET draw them. Mean +/- 2 standard deviations:
    // a month outside is outside this site's OWN normal range, rather than an invented threshold.
    const vals = pts.map((p) => p.pct);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const sd = Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length);
    const upper = mean + 2 * sd;
    const lower = mean - 2 * sd;
    // A margin is a policy target and is symmetric about zero; +/-2 sigma is a statistic about this
    // site's own spread and sits wherever its history puts it. They answer different questions, so
    // both are drawn -- but only one can decide the colour of a bar, and that is the margin.
    const margin = (typeof marginPct === 'number' && isFinite(marginPct) && marginPct > 0)
      ? marginPct : DEFAULT_MB_MARGIN_PCT;
    const isDefault = !(typeof marginPct === 'number' && isFinite(marginPct) && marginPct > 0);
    const inBand = (v: number) => Math.abs(v) <= margin;

    const data = pts.map((p) => ({
      period: p.period,
      label: p.period.slice(2).replace('-', '/'),   // 2026-08 -> 26/08
      value: mode === 'pct' ? p.pct : p.mass,
      rolling: mode === 'pct' ? p.roll.pct : p.roll.mass,
      pct: p.pct,
      mass: p.mass,
      rollPct: p.roll.pct,
      inLimits: inBand(p.pct),
    }));

    const latest = data[data.length - 1];
    const worst = data.reduce((a, b) => (a.pct < b.pct ? a : b));   // most negative = biggest loss
    const outside = data.filter((d) => !d.inLimits).length;
    return { data, mean, sd, upper, lower, margin, isDefault, latest, worst, outside, n: data.length };
  }, [series, months, mode, marginPct]);

  if (!model) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Mass Gain/Loss</Typography>
        <Typography variant="caption" color="text.secondary">
          Needs at least two months of fuel &amp; loss history for this dataset.
        </Typography>
      </Paper>
    );
  }

  const { data, mean, upper, lower, margin, isDefault, latest, worst, outside, n } = model;
  const fmtPct = (v: number | null) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`);
  const fmtMass = (v: number | null) =>
    (v == null ? '—' : `${v >= 0 ? '+' : ''}${Math.round(v).toLocaleString()}`);
  const show = (d: any) => (mode === 'pct' ? fmtPct(d.pct) : fmtMass(d.mass));
  const targetColor = theme.palette.mode === 'dark' ? theme.palette.text.primary : '#000';

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Mass Gain/Loss</Typography>
        <Typography variant="caption" color="text.secondary">
          overall health of the process · last {n} months
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, v) => v && setMode(v)}>
          <ToggleButton value="pct" sx={{ py: 0.1, px: 1.2, fontSize: '0.7rem' }}>%</ToggleButton>
          <ToggleButton value="mass" sx={{ py: 0.1, px: 1.2, fontSize: '0.7rem' }}>{massUnit}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Headline first, detail underneath — the Meter Health Overview pattern. */}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">{latest.period}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: latest.inLimits ? GREEN : RED, lineHeight: 1.1 }}>
            {show(latest)}
          </Typography>
          <Chip size="small" label={latest.inLimits ? 'Within target' : 'Outside target'}
            color={latest.inLimits ? 'success' : 'error'} sx={{ height: 20, mt: 0.5 }} />
        </Box>
        {[
          { k: `${n}-month average`, v: fmtPct(mean) },
          { k: 'Worst month', v: `${worst.period} · ${fmtPct(worst.pct)}` },
          { k: 'Months outside target', v: `${outside} of ${n}` },
        ].map((c) => (
          <Box key={c.k}>
            <Typography variant="caption" color="text.secondary">{c.k}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{c.v}</Typography>
          </Box>
        ))}
      </Stack>

      <ResponsiveContainer width="100%" height={230}>
        {/* The right gutter has to hold the reference-line labels, which sit OUTSIDE the plot
            ("target -0.50%" is ~85px at 10px). It was 16px, so every one of them was clipped to a
            couple of characters. */}
        <ComposedChart data={data} margin={{ top: 18, right: 104, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }}
            tickFormatter={(v) => (mode === 'pct' ? `${Number(v).toFixed(2)}%` : Number(v).toLocaleString())}
            label={{ value: mode === 'pct' ? 'Mass Gain/Loss (%)' : `Mass Gain/Loss (${massUnit})`,
                     angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
          <RTooltip
            formatter={(v: any, n: any, p: any) => (n === '12-month rolling average'
              ? [mode === 'pct' ? fmtPct(Number(v)) : `${fmtMass(Number(v))} ${massUnit}`, n]
              : [`${fmtPct(p.payload.pct)}${p.payload.mass != null ? ` · ${fmtMass(p.payload.mass)} ${massUnit}` : ''}`,
                 p.payload.inLimits ? 'Within target' : 'Outside target'])}
            labelFormatter={(l: any) => `Period ${data.find((d) => d.label === l)?.period ?? l}`} />
          <ReferenceLine y={0} stroke={theme.palette.text.primary} />
          {/* +/-2 sigma: a REFERENCE showing this site's own statistical spread. Leslie (CalWork, 15 Oct):
              the faint dotted grey was "hard to see" -- now mid-grey, longer dashes and readable labels,
              but still lighter than the black target, which is the line the bars are judged against. */}
          {mode === 'pct' && [
            { y: upper, lab: `+2σ ${fmtPct(upper)}` },
            { y: lower, lab: `−2σ ${fmtPct(lower)}` },
          ].map((r) => (
            <ReferenceLine key={r.lab} y={r.y} stroke={theme.palette.text.secondary} strokeWidth={1.5} strokeDasharray="4 3"
              label={{ value: r.lab, position: 'right', style: { fontSize: 11, fontWeight: 600, fill: theme.palette.text.secondary } }} />
          ))}
          {/* The configured target — what actually decides green vs red. Black and dashed (Leslie,
              15 Oct); dashed is what separates it from the solid zero line in the same colour. In dark
              mode "black" would vanish, so it takes the theme's primary text colour there instead. */}
          {mode === 'pct' && [margin, -margin].map((y) => (
            <ReferenceLine key={`m${y}`} y={y} stroke={targetColor} strokeWidth={2} strokeDasharray="6 3"
              label={{ value: `target ${fmtPct(y)}`, position: 'right',
                       style: { fontSize: 11, fontWeight: 700, fill: targetColor } }} />
          ))}
          <Bar dataKey="value" radius={[2, 2, 0, 0]} isAnimationActive={false}>
            {data.map((d) => <Cell key={d.period} fill={d.inLimits ? GREEN : RED} />)}
            <LabelList dataKey="value" position="top" style={{ fontSize: 10, fontWeight: 600 }}
              formatter={(v: any) => (mode === 'pct' ? `${Number(v).toFixed(2)}%` : Math.round(Number(v)).toLocaleString())} />
          </Bar>
          {/* "12 month rolling average of Unaccounted Loss, %" — asked for alongside the monthly
              bars. Trailing 12 months, so it needs the series BEFORE the window on screen. */}
          <Line type="monotone" dataKey="rolling" name="12-month rolling average" dot={false}
            stroke="#1565c0" strokeWidth={2} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>

      <Tooltip title={`Green/red is judged against the Mass Balance Target of ±${fmtPct(margin)}${isDefault ? ' (the default — set one for this dataset in Settings → Mass Balance)' : ' configured for this dataset'}. The ±2σ band is shown as a reference: it is where this site's own 12-month spread sits, not a target.`}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, cursor: 'help' }}>
          Green = within target · red = outside · blue line = 12-month rolling average · black dashed = target.{' '}
          Target <strong>±{fmtPct(margin)}</strong>{isDefault ? ' (default)' : ''}; grey dashed = ±2σ, shown for reference ({fmtPct(lower)} to {fmtPct(upper)}).
          {' '}A loss shows negative; percent is of crude processed.
        </Typography>
      </Tooltip>
    </Paper>
  );
};

export default MassGainLossOverview;
